import type { AnyAction, DeepPartial, EnhancedStore, MiddlewareArray } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import type { ThunkMiddlewareFor } from '@reduxjs/toolkit/dist/getDefaultMiddleware';
import { setupListeners } from '@reduxjs/toolkit/query';
import type { Account, User } from '@foundershub/api-client';
import { api, useConfigQuery, useUserQuery } from '@foundershub/api-client';
import { api as oembedApi } from '@foundershub/oembed';
import { AuthError } from './errors';
import type { Slice as AccountSlice } from './slices/account';
import account, { useAccount } from './slices/account';
import type { Slice as RequestHeadersSlice } from './slices/request-headers';
import requestHeaders from './slices/request-headers';

export { useDispatch } from 'react-redux';

export const {
  actions: { setAccount },
} = account;

export type Store = EnhancedStore<
  {
    account: AccountSlice;
    requestHeaders: RequestHeadersSlice;
  },
  AnyAction,
  MiddlewareArray<ThunkMiddlewareFor<unknown>>
>;

export function getStore(preloadedState?: DeepPartial<Record<string, unknown>>): Store {
  const store = configureStore({
    reducer: {
      [account.name]: account.reducer,
      [requestHeaders.name]: requestHeaders.reducer,
      [api.reducerPath]: api.reducer,
      [oembedApi.reducerPath]: oembedApi.reducer,
    },
    devTools: process.env.NODE_ENV !== 'production',
    preloadedState,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(api.middleware, oembedApi.middleware),
  });

  setupListeners(store.dispatch);

  return store;
}

export { Provider } from 'react-redux';

export function useUserRequired() {
  const { data } = useUserQuery();
  if (!data) {
    throw new AuthError();
  }

  return data as User;
}

export function useAccountRequired() {
  const account = useAccount();
  if (!account) {
    throw new AuthError();
  }

  return account as Account;
}

export function useFeatureFlagEnabled(flag: string) {
  const { data } = useConfigQuery();
  if (!data || !(flag in data)) {
    return false;
  }

  return Boolean(data[flag]);
}

export * from './slices/account';
